import { Box } from '@mui/material';

import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Loader } from 'common/components/Loader/Loader';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import {
	setToastMessage,
	setMyApprovalData,
} from 'common/store/reducer/commonReducer';

import { getMyApprovalChangesTabData } from 'features/HomePage/api';
import { IActionReviewPayload } from 'features/HomePage/api/type-def';

import {
	IOverviewProps,
	OPTION_VALUES,
	ACTION_VALUES,
} from '../MyApprovalUtils';

import ChangeItem from './ChangeItem';

import styles from './MyApprovalsChangesTab.module.scss';

const MyApprovalChangesTab = (props: IOverviewProps) => {
	const { entityId, entityType, entityName } = props;
	const myApprovalData = useSelector(
		(state: RootState) => state.common.myApprovalsData,
	);
	const [changesTabData, setChangesTabData] =
		useState<IActionReviewPayload[]>(myApprovalData);
	const [loading, setIsLoading] = useState<boolean>(false);
	const { OK, FORBIDDEN } = HTTP_STATUS_CODES;
	const { APPROVE } = ACTION_VALUES;
	const { APPROVE_VALUES, REJECT_VALUES, REQUEST_CHANGES_VALUES, PENDING } =
		OPTION_VALUES;
	const dispatch = useDispatch();

	const activeTabIndex = useSelector(
		(state: RootState) => state.common.myApprovalsSelectedTabIndex,
	);

	const getStatusName = () => {
		switch (activeTabIndex) {
			case 0:
				return `${PENDING}, ${REQUEST_CHANGES_VALUES}`;
			case 1:
				return APPROVE_VALUES;
			case 2:
				return REJECT_VALUES;
			default:
				return '';
		}
	};

	const fetchMyApprovalsChangesTabData = async () => {
		setIsLoading(true);
		const response = await getMyApprovalChangesTabData(
			entityId,
			getStatusName(),
		);
		if (response?.statusCode === OK && response?.responseBody) {
			setChangesTabData(response?.responseBody);
			dispatch(setMyApprovalData(response?.responseBody));
		} else if (
			response?.statusCode !== FORBIDDEN &&
			response?.statusCode !== OK
		) {
			dispatch(
				setToastMessage({
					message: response?.errorBody?.errorMessage || APP_GENERAL_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
		setIsLoading(false);
	};

	useEffect(() => {
		if (changesTabData?.length === 0) {
			fetchMyApprovalsChangesTabData();
		}
	}, []);

	const handleChangesSubmitted = (
		action: string,
		id: string,
		isSubmitted: boolean,
		comment: string,
		dueDate: string,
		priority: string,
		status: string,
		createdDate: string,
		commentBy: string,
		shouldAbleToChangeApproval: boolean,
	) => {
		const updatedItems = changesTabData.map((res) => {
			if (res.id === id) {
				return {
					...res,
					isSubmitted,
					isApproved: action === APPROVE,
					shouldAbleToChangeApproval,
					comments: [
						...res.comments,
						{
							commentsId: id,
							comments: comment,
							priority,
							dueDate,
							status,
							createdDate,
							commentBy,
						},
					],
				};
			}
			return res;
		});
		setChangesTabData(updatedItems);
		dispatch(setMyApprovalData(updatedItems));
	};

	return (
		<Box className={styles['change-item-container']}>
			{loading && <Loader show />}
			{changesTabData &&
				changesTabData.map((change) => (
					<ChangeItem
						key={change?.id}
						changeObject={change}
						entityId={entityId}
						entityName={entityName || ''}
						entityType={entityType}
						isChangesSubmitted={
							change.comments.length !== 0 || change.isSubmitted || false
						}
						isApprovedChanges={
							activeTabIndex === 1 || change.isApproved || false
						}
						shouldAbleToChangeApproval={
							activeTabIndex === 1 ||
							activeTabIndex === 2 ||
							change.shouldAbleToChangeApproval ||
							false
						}
						submitChangesHandler={handleChangesSubmitted}
					/>
				))}
		</Box>
	);
};

export default MyApprovalChangesTab;
